import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSecurityAlerts } from '../../modules/audit/hooks';
import {
  SecurityAlert,
  SEVERITY_COLORS,
  SEVERITY_DOT,
  SEVERITY_LABEL,
  AlertType,
  IncidentSeverity,
} from '../../modules/audit/models';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import {
  Server,
  AlertTriangle,
  CheckCircle,
  Activity,
  RefreshCw,
  Bell,
  BellOff,
  Shield,
  Database,
  Globe,
  Cpu,
  Clock,
  GitBranch,
  Lock,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ServiceHealth {
  name: string;
  icon: React.ReactNode;
  status: 'operational' | 'warning' | 'degraded' | 'loading';
  detail: string;
}

interface SuspiciousIP {
  ip_address: string;
  portal: string | null;
  count: number;
  last_seen: string;
  flagged: boolean;
}

type MainTab = 'overview' | 'deployments' | 'schema';

interface DeploymentRow {
  id: string;
  source_system: string | null;
  title: string;
  message: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

interface SchemaSnapshot {
  id: string;
  snapshot_type: string;
  environment: string | null;
  integrity_hash: string;
  created_at: string;
  description: string | null;
  is_baseline: boolean;
}

interface CreateAlertForm {
  alert_type: AlertType;
  severity: IncidentSeverity;
  title: string;
  message: string;
  source_system: string;
  expires_at: string;
}

const BLANK_FORM: CreateAlertForm = {
  alert_type: 'security',
  severity: 'medium',
  title: '',
  message: '',
  source_system: '',
  expires_at: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function StatusBadge({ status }: { status: ServiceHealth['status'] }) {
  if (status === 'loading') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" />
        Checking…
      </span>
    );
  }
  const map = {
    operational: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    degraded: 'bg-red-100 text-red-800',
  } as const;
  const dotMap = {
    operational: 'bg-green-500',
    warning: 'bg-yellow-500',
    degraded: 'bg-red-500',
  } as const;
  const label = {
    operational: 'Operational',
    warning: 'Warning',
    degraded: 'Degraded',
  } as const;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${map[status]}`}>
      <span className={`w-2 h-2 rounded-full ${dotMap[status]}`} />
      {label[status]}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SecurityInfrastructure() {
  const { user } = useAuth();
  const { alerts, loading: alertsLoading, acknowledgeAlert, createAlert, refetch: refetchAlerts } = useSecurityAlerts(false);

  const [activeTab, setActiveTab] = useState<MainTab>('overview');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Deployments
  const [deployments, setDeployments] = useState<DeploymentRow[]>([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);

  // Schema snapshots
  const [schemaSnaps, setSchemaSnaps] = useState<SchemaSnapshot[]>([]);
  const [schemaLoading, setSchemaLoading] = useState(false);

  // Service health
  const [health, setHealth] = useState<ServiceHealth[]>([
    { name: 'Supabase DB', icon: <Database className="w-5 h-5 text-blue-600" />, status: 'loading', detail: '' },
    { name: 'PayPal Webhooks', icon: <Globe className="w-5 h-5 text-blue-600" />, status: 'loading', detail: '' },
    { name: 'Authentication', icon: <Shield className="w-5 h-5 text-blue-600" />, status: 'loading', detail: '' },
    { name: 'Billing', icon: <Cpu className="w-5 h-5 text-blue-600" />, status: 'loading', detail: '' },
  ]);

  // Suspicious IPs
  const [suspiciousIPs, setSuspiciousIPs] = useState<SuspiciousIP[]>([]);
  const [ipsLoading, setIpsLoading] = useState(true);

  // Create alert modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState<CreateAlertForm>(BLANK_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Load service health ──────────────────────────────────────────────────────

  const loadHealth = useCallback(async () => {
    setHealth(prev => prev.map(h => ({ ...h, status: 'loading' as const, detail: '' })));

    // 1) Supabase DB — query login_records
    const dbCheck = supabase.from('login_records').select('id', { count: 'exact', head: true });

    // 2) PayPal Webhooks — unprocessed count
    const ppCheck = supabase
      .from('paypal_webhook_events')
      .select('id', { count: 'exact', head: true })
      .eq('processed', false);

    // 3) Auth — logins in last hour
    const hourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const authCheck = supabase
      .from('login_records')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', hourAgo);

    // 4) Billing — active PayPal subscriptions
    const billingCheck = supabase
      .from('paypal_billing')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'active');

    const [dbRes, ppRes, authRes, billingRes] = await Promise.all([dbCheck, ppCheck, authCheck, billingCheck]);

    const updated: ServiceHealth[] = [
      {
        name: 'Supabase DB',
        icon: <Database className="w-5 h-5 text-blue-600" />,
        status: dbRes.error ? 'degraded' : 'operational',
        detail: dbRes.error ? dbRes.error.message : 'Operational',
      },
      {
        name: 'PayPal Webhooks',
        icon: <Globe className="w-5 h-5 text-blue-600" />,
        status: ppRes.error
          ? 'degraded'
          : (ppRes.count ?? 0) > 10
          ? 'warning'
          : 'operational',
        detail: ppRes.error
          ? ppRes.error.message
          : `${ppRes.count ?? 0} unprocessed event${ppRes.count === 1 ? '' : 's'}`,
      },
      {
        name: 'Authentication',
        icon: <Shield className="w-5 h-5 text-blue-600" />,
        status: authRes.error ? 'degraded' : 'operational',
        detail: authRes.error
          ? authRes.error.message
          : `Active — ${authRes.count ?? 0} login${authRes.count === 1 ? '' : 's'}/hr`,
      },
      {
        name: 'Billing',
        icon: <Cpu className="w-5 h-5 text-blue-600" />,
        status: billingRes.error ? 'degraded' : 'operational',
        detail: billingRes.error
          ? billingRes.error.message
          : `${billingRes.count ?? 0} active subscription${billingRes.count === 1 ? '' : 's'}`,
      },
    ];

    setHealth(updated);
  }, []);

  // ── Load suspicious IPs ──────────────────────────────────────────────────────

  const loadSuspiciousIPs = useCallback(async () => {
    setIpsLoading(true);
    const since24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('login_records')
      .select('ip_address, portal, created_at')
      .gte('created_at', since24h)
      .not('ip_address', 'is', null);

    if (!error && data) {
      // Group by ip_address
      const ipMap: Record<string, { portal: string | null; count: number; last_seen: string }> = {};
      for (const row of data) {
        const ip = row.ip_address as string;
        if (!ipMap[ip]) {
          ipMap[ip] = { portal: row.portal, count: 0, last_seen: row.created_at };
        }
        ipMap[ip].count++;
        if (row.created_at > ipMap[ip].last_seen) {
          ipMap[ip].last_seen = row.created_at;
        }
      }

      const rows: SuspiciousIP[] = Object.entries(ipMap)
        .map(([ip, v]) => ({ ip_address: ip, portal: v.portal, count: v.count, last_seen: v.last_seen, flagged: v.count > 5 }))
        .sort((a, b) => b.count - a.count);

      setSuspiciousIPs(rows);
    }
    setIpsLoading(false);
  }, []);

  const loadDeployments = useCallback(async () => {
    setDeploymentsLoading(true);
    const { data } = await supabase
      .from('security_alerts')
      .select('id, source_system, title, message, created_at, metadata')
      .in('source_system', ['netlify', 'github'])
      .order('created_at', { ascending: false })
      .limit(50);
    setDeployments((data ?? []) as DeploymentRow[]);
    setDeploymentsLoading(false);
  }, []);

  const loadSchemaSnapshots = useCallback(async () => {
    setSchemaLoading(true);
    const { data } = await supabase
      .from('audit_snapshots')
      .select('id, snapshot_type, environment, integrity_hash, created_at, description, is_baseline')
      .order('created_at', { ascending: false })
      .limit(30);
    setSchemaSnaps((data ?? []) as SchemaSnapshot[]);
    setSchemaLoading(false);
  }, []);

  useEffect(() => {
    loadHealth();
    loadSuspiciousIPs();
  }, [loadHealth, loadSuspiciousIPs]);

  useEffect(() => {
    if (activeTab === 'deployments') loadDeployments();
    if (activeTab === 'schema') loadSchemaSnapshots();
  }, [activeTab, loadDeployments, loadSchemaSnapshots]);

  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        loadHealth();
        loadSuspiciousIPs();
        refetchAlerts();
        if (activeTab === 'deployments') loadDeployments();
        if (activeTab === 'schema') loadSchemaSnapshots();
      }, 30000);
    } else {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    }
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [autoRefresh, activeTab, loadHealth, loadSuspiciousIPs, refetchAlerts, loadDeployments, loadSchemaSnapshots]);

  // ── Acknowledge alert ────────────────────────────────────────────────────────

  const handleAcknowledge = async (alertId: string) => {
    if (!user?.id) return;
    await acknowledgeAlert(alertId, user.id);
  };

  // ── Create alert ─────────────────────────────────────────────────────────────

  const handleCreateAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      setFormError('Title and message are required.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await createAlert({
        alert_type: form.alert_type,
        severity: form.severity,
        title: form.title.trim(),
        message: form.message.trim(),
        source_system: form.source_system.trim() || null,
        expires_at: form.expires_at || null,
        acknowledged_by: null,
        acknowledged_at: null,
        related_incident_id: null,
        metadata: null,
      });
      setForm(BLANK_FORM);
      setShowCreateModal(false);
      refetchAlerts();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const activeAlerts = alerts.filter(a => !a.acknowledged_at);
  const allAlerts = [...alerts].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const thClass = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
  const tdClass = 'px-4 py-3 whitespace-nowrap text-sm text-gray-700';

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Server className="w-6 h-6 text-gray-700" />
              Security Infrastructure
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Infrastructure health, alert management, and suspicious activity monitoring
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${autoRefresh ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            >
              {autoRefresh ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </button>
            <button
              onClick={() => { loadHealth(); loadSuspiciousIPs(); refetchAlerts(); }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {([['overview', 'Overview'], ['deployments', 'Deployments'], ['schema', 'Schema Protection']] as [MainTab, string][]).map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'deployments' && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <GitBranch className="w-5 h-5 text-gray-500" />
              <h2 className="text-base font-semibold text-gray-800">Deployment Events (Netlify / GitHub)</h2>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {deploymentsLoading ? (
                <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-700" /></div>
              ) : deployments.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">
                  <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No deployment events found. Create security alerts with source_system &quot;netlify&quot; or &quot;github&quot; to populate this view.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={thClass}>Source</th>
                        <th className={thClass}>Title</th>
                        <th className={thClass}>Message</th>
                        <th className={thClass}>Created At</th>
                        <th className={thClass}>Metadata</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {deployments.map(d => (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className={`${tdClass}`}>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{d.source_system ?? '—'}</span>
                          </td>
                          <td className={tdClass}>{d.title}</td>
                          <td className={`${tdClass} text-gray-500 max-w-xs truncate`}>{d.message}</td>
                          <td className={tdClass}>{formatDate(d.created_at)}</td>
                          <td className={tdClass}>
                            {d.metadata && Object.keys(d.metadata).length > 0 ? (
                              <code className="text-xs text-gray-500">{JSON.stringify(d.metadata).slice(0, 40)}…</code>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'schema' && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-gray-500" />
              <h2 className="text-base font-semibold text-gray-800">Schema Protection Monitor</h2>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {schemaLoading ? (
                <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-700" /></div>
              ) : schemaSnaps.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">
                  <Database className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No schema snapshots found. Capture snapshots from Security Recovery to monitor schema drift.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={thClass}>Type</th>
                        <th className={thClass}>Environment</th>
                        <th className={thClass}>Hash</th>
                        <th className={thClass}>Baseline</th>
                        <th className={thClass}>Description</th>
                        <th className={thClass}>Captured At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {schemaSnaps.map(s => (
                        <tr key={s.id} className="hover:bg-gray-50">
                          <td className={tdClass}>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{s.snapshot_type}</span>
                          </td>
                          <td className={tdClass}>{s.environment ?? '—'}</td>
                          <td className={`${tdClass} font-mono text-xs text-gray-500`}>{s.integrity_hash.slice(0, 16)}</td>
                          <td className={tdClass}>
                            {s.is_baseline ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium"><CheckCircle className="w-3 h-3" />Yes</span>
                            ) : <span className="text-gray-400 text-xs">No</span>}
                          </td>
                          <td className={`${tdClass} text-gray-500 max-w-xs truncate`}>{s.description ?? '—'}</td>
                          <td className={tdClass}>{formatDate(s.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'overview' && (<>

        {/* ── Service Health Grid ── */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-gray-500" />
            Service Health
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {health.map(svc => (
              <div key={svc.name} className="bg-white rounded-lg border border-gray-200 p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                    {svc.icon}
                  </div>
                  <span className="font-medium text-gray-900 text-sm">{svc.name}</span>
                </div>
                <StatusBadge status={svc.status} />
                {svc.detail && (
                  <p className="text-xs text-gray-500 truncate">{svc.detail}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ── Active Alerts ── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Bell className="w-4 h-4 text-gray-500" />
              Active Alerts
              {activeAlerts.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                  {activeAlerts.length}
                </span>
              )}
            </h2>
            <button
              onClick={() => { setForm(BLANK_FORM); setFormError(null); setShowCreateModal(true); }}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Bell className="w-3.5 h-3.5" />
              Create Alert
            </button>
          </div>

          {alertsLoading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-700" />
            </div>
          ) : activeAlerts.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500 text-sm flex flex-col items-center gap-2">
              <BellOff className="w-8 h-8 text-gray-300" />
              No active alerts
            </div>
          ) : (
            <div className="space-y-3">
              {activeAlerts.map(alert => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onAcknowledge={handleAcknowledge}
                />
              ))}
            </div>
          )}
        </section>

        {/* ── Suspicious Login Activity ── */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-gray-500" />
            Suspicious Login Activity (Last 24h)
          </h2>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>IP Address</th>
                    <th className={thClass}>Portal</th>
                    <th className={thClass}>Logins (24h)</th>
                    <th className={thClass}>Last Seen</th>
                    <th className={thClass}>Flag</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ipsLoading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-700" />
                        </div>
                      </td>
                    </tr>
                  ) : suspiciousIPs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500 text-sm">
                        No login activity in the last 24 hours.
                      </td>
                    </tr>
                  ) : (
                    suspiciousIPs.map(row => (
                      <tr key={row.ip_address} className="hover:bg-gray-50">
                        <td className={`${tdClass} font-mono text-xs`}>{row.ip_address}</td>
                        <td className={tdClass}>
                          {row.portal ? (
                            <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                              {row.portal}
                            </span>
                          ) : '—'}
                        </td>
                        <td className={tdClass}>
                          <span className={`font-semibold ${row.flagged ? 'text-red-600' : 'text-gray-900'}`}>
                            {row.count}
                          </span>
                        </td>
                        <td className={tdClass}>{formatDate(row.last_seen)}</td>
                        <td className={tdClass}>
                          {row.flagged ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                              <AlertTriangle className="w-3 h-3" />
                              Suspicious
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Normal
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── Alert History ── */}
        <section className="pb-4">
          <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            Alert History
          </h2>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Severity</th>
                    <th className={thClass}>Title</th>
                    <th className={thClass}>Source</th>
                    <th className={thClass}>Created</th>
                    <th className={thClass}>Acknowledged By</th>
                    <th className={thClass}>Acknowledged At</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {alertsLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-700" />
                        </div>
                      </td>
                    </tr>
                  ) : allAlerts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500 text-sm">
                        No alert history.
                      </td>
                    </tr>
                  ) : (
                    allAlerts.map(a => (
                      <tr key={a.id} className="hover:bg-gray-50">
                        <td className={tdClass}>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[a.severity]}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${SEVERITY_DOT[a.severity]}`} />
                            {SEVERITY_LABEL[a.severity]}
                          </span>
                        </td>
                        <td className={tdClass}>{a.title}</td>
                        <td className={`${tdClass} text-gray-500`}>{a.source_system ?? '—'}</td>
                        <td className={tdClass}>{formatDate(a.created_at)}</td>
                        <td className={`${tdClass} font-mono text-xs text-gray-500`}>
                          {a.acknowledged_by ? `${a.acknowledged_by.slice(0, 8)}…` : '—'}
                        </td>
                        <td className={tdClass}>{formatDate(a.acknowledged_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        </>)}

      </div>

      {/* ── Create Alert Modal ── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Create Security Alert
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAlert} className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Alert Type</label>
                  <select
                    value={form.alert_type}
                    onChange={e => setForm(f => ({ ...f, alert_type: e.target.value as AlertType }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                  >
                    <option value="security">Security</option>
                    <option value="billing">Billing</option>
                    <option value="system">System</option>
                    <option value="compliance">Compliance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Severity</label>
                  <select
                    value={form.severity}
                    onChange={e => setForm(f => ({ ...f, severity: e.target.value as IncidentSeverity }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                  placeholder="Brief alert title"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Message <span className="text-red-500">*</span></label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
                  placeholder="Describe the alert…"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Source System</label>
                <input
                  type="text"
                  value={form.source_system}
                  onChange={e => setForm(f => ({ ...f, source_system: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                  placeholder="e.g. auth-service, paypal, admin-panel"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expires At (optional)</label>
                <input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
                  Create Alert
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

// ─── AlertCard ────────────────────────────────────────────────────────────────

function AlertCard({
  alert,
  onAcknowledge,
}: {
  alert: SecurityAlert;
  onAcknowledge: (id: string) => Promise<void>;
}) {
  const [acking, setAcking] = useState(false);

  const handleAck = async () => {
    setAcking(true);
    await onAcknowledge(alert.id);
    setAcking(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-start gap-4">
      <span className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${SEVERITY_DOT[alert.severity]}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 text-sm">{alert.title}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[alert.severity]}`}>
            {SEVERITY_LABEL[alert.severity]}
          </span>
        </div>
        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 flex-wrap">
          {alert.source_system && (
            <span className="flex items-center gap-1">
              <Server className="w-3 h-3" />
              {alert.source_system}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDate(alert.created_at)}
          </span>
        </div>
      </div>
      <button
        onClick={handleAck}
        disabled={acking}
        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
      >
        {acking ? (
          <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500" />
        ) : (
          <CheckCircle className="w-3.5 h-3.5" />
        )}
        Acknowledge
      </button>
    </div>
  );
}
